(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('tf-shop-config').textContent);
  let queue = Promise.resolve();
  const busy = new WeakSet();
  const pendingBundles = new WeakMap();
  let feedbackTimer;
  let cartSync;
  const sectionNames = ['tf-cart-drawer', 'tf-cart-page'];
  const dispatchProperty = 'Availability & dispatch';
  const sectionRequest = () => ({ sections: sectionNames, sections_url: config.cart });
  const sortedProperties = properties => Object.fromEntries(Object.entries(properties || {}).filter(([, value]) => value != null && value !== '').sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => [key, String(value)]));
  const productProperties = properties => sortedProperties(Object.fromEntries(Object.entries(properties || {}).filter(([key]) => !['_tf_request', '_tf_cart', dispatchProperty].includes(key))));
  const identity = item => JSON.stringify([String(item.variant_id || item.id), String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''), productProperties(item.properties)]);
  const quantities = cart => {
    const totals = new Map();
    for (const item of cart.items || []) totals.set(identity(item), (totals.get(identity(item)) || 0) + item.quantity);
    return JSON.stringify([...totals].sort(([a], [b]) => a.localeCompare(b)));
  };
  const money = cents => new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency }).format(cents / 100);
  const serialize = work => { const task = queue.then(() => navigator.locks?.request ? navigator.locks.request('tf-cart', work) : work()); queue = task.catch(() => {}); return task; };
  const live = text => { document.getElementById('tf-live-status').textContent = text; };
  function addedFeedback() {
    const feedback = document.getElementById('tf-cart-feedback');
    live(feedback?.textContent.trim() || 'Added to cart');
    if (!feedback) return;
    clearTimeout(feedbackTimer);
    feedback.classList.add('is-visible');
    feedbackTimer = setTimeout(() => feedback.classList.remove('is-visible'), 3000);
  }
  async function request(url, body) {
    const response = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok) { const error = new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'Your cart could not be updated.'); error.confirmed = true; throw error; }
    return data;
  }
  async function cartSections(sections) {
    if (!sections || sectionNames.some(name => typeof sections[name] !== 'string')) {
      const url = new URL(config.cart, location.origin); url.searchParams.set('sections', sectionNames.join(','));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Your cart changed, but the display could not refresh. Reload the page to see your cart.');
      sections = await response.json();
    }
    return sections;
  }
  async function normalizeCart(cart) {
    if (!Array.isArray(cart.items)) cart = { ...await cartState(), sections: cart.sections };
    const before = quantities(cart);
    const maxPasses = cart.items.length * 2 + 3;
    for (let pass = 0; pass < maxPasses; pass++) {
      const groups = new Map();
      for (const item of cart.items) {
        // Preserve custom options, selling plans and native price splits.
        const group = JSON.stringify([identity(item), item.original_price, item.final_price]);
        if (!groups.has(group)) groups.set(group, []);
        groups.get(group).push(item);
      }
      const updates = {};
      for (const lines of groups.values()) {
        if (lines.length < 2) continue;
        updates[lines[0].key] = lines.reduce((sum, line) => sum + line.quantity, 0);
        for (const line of lines.slice(1)) updates[line.key] = 0;
      }
      if (Object.keys(updates).length) {
        cart = await request(config.cart + '/update.js', { updates, ...sectionRequest() });
      } else {
        cart.sections = await cartSections(cart.sections);
        const doc = new DOMParser().parseFromString(cart.sections['tf-cart-drawer'], 'text/html');
        const notes = new Map([...doc.querySelectorAll('[data-cart-key][data-cart-dispatch]')].map(row => [row.dataset.cartKey, row.dataset.cartDispatch]));
        let changed = false;
        for (const line of cart.items) {
          if (!notes.has(line.key)) throw new Error('We couldn’t refresh delivery details. Please reopen your cart and try again.');
          const properties = { ...productProperties(line.properties), _tf_cart: '1' };
          if (notes.get(line.key)) properties[dispatchProperty] = notes.get(line.key);
          if (JSON.stringify(sortedProperties(properties)) === JSON.stringify(sortedProperties(line.properties))) continue;
          // Keep all customer properties; only our request and delivery fields change.
          cart = await request(config.change + '.js', { id: line.key, quantity: line.quantity, properties, ...sectionRequest() });
          changed = true;
          break; // Properties change the key, so read the fresh cart before the next update.
        }
        if (!changed) return cart;
      }
      if (quantities(cart) !== before) throw new Error('Your cart changed while it was updating. Please review its quantities before checking out.');
    }
    throw new Error('Your cart changed while delivery details were updating. Please reopen it and try again.');
  }
  async function refresh(sections) {
    sections = await cartSections(sections);
    for (const [sectionName, instance] of [['tf-cart-drawer', 'drawer'], ['tf-cart-page', 'page']]) {
      const target = document.querySelector(`[data-tf-cart-content="${instance}"]`);
      if (!target) continue;
      const doc = new DOMParser().parseFromString(sections[sectionName] || '', 'text/html');
      const fresh = doc.querySelector(`[data-tf-cart-content="${instance}"]`);
      if (!fresh) throw new Error('Your cart changed. Reload the page to see the latest items.');
      target.replaceChildren(...fresh.childNodes);
      target.dataset.deliveryBackorder = fresh.dataset.deliveryBackorder || 'false';
    }
    const response = await fetch(config.cart + '.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Your cart changed. Reload to see the updated total.');
    const cart = await response.json();
    document.querySelectorAll('.tf-site-cart-badge').forEach(el => { el.textContent = cart.item_count; el.hidden = !cart.item_count; });
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items'); });
    return cart;
  }
  function syncCart() {
    if (cartSync) return cartSync;
    const contents = [...document.querySelectorAll('[data-tf-cart-content]')];
    const controls = contents.flatMap(cart => [...cart.querySelectorAll('button,input')].map(el => [el, el.disabled]));
    contents.forEach(cart => {
      cart.setAttribute('aria-busy', 'true');
      const error = cart.querySelector('[data-cart-error]');
      if (error) error.hidden = true;
    });
    controls.forEach(([el]) => el.disabled = true);
    cartSync = serialize(async () => {
      const result = await normalizeCart(await request(config.cart + '/update.js', { updates: {}, ...sectionRequest() }));
      return refresh(result.sections);
    }).finally(() => {
      contents.forEach(cart => cart.removeAttribute('aria-busy'));
      controls.forEach(([el, disabled]) => { if (el.isConnected) el.disabled = disabled; });
      cartSync = null;
    });
    return cartSync;
  }
  function syncVisibleCart() {
    syncCart().catch(() => {
      const message = 'We couldn’t refresh your cart. Please reopen it or try again before checking out.';
      document.querySelectorAll('[data-cart-error]').forEach(error => { error.textContent = message; error.hidden = false; });
      live(message);
    });
  }
  function openCart() { document.getElementById('tf-cart-open').click(); }
  async function cartState() {
    const response = await fetch(config.cart + '.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Please reload your cart before trying again.');
    return response.json();
  }
  const add = (items, open = false, attempt = {}) => serialize(async () => {
    if (!items.length || items.some(item => !Number.isSafeInteger(Number(item.id)) || Number(item.id) <= 0 || !Number.isInteger(item.quantity) || item.quantity < 1)) throw new Error('Please choose an available product option.');
    if (attempt.uncertain) throw new Error('This request may already have reached your cart. Open your cart and check it before adding again.');
    const before = await cartState();
    const grouped = new Map();
    for (const item of items) {
      const key = identity(item);
      if (grouped.has(key)) grouped.get(key).quantity += item.quantity;
      else grouped.set(key, { ...item, properties: productProperties(item.properties) });
    }
    const requested = [...grouped.values()];
    const additions = requested.map(item => {
      const existing = before.items.find(line => identity(line) === identity(item));
      const properties = { ...item.properties, _tf_cart: '1' };
      if (existing?.properties?.[dispatchProperty]) properties[dispatchProperty] = existing.properties[dispatchProperty];
      return { ...item, properties };
    });
    let result;
    try { result = await request(config.add + '.js', { items: additions, ...sectionRequest() }); }
    catch (error) {
      attempt.uncertain = !error.confirmed;
      try {
        const cart = await cartState();
        const total = (state, item) => state.items.filter(line => identity(line) === identity(item)).reduce((sum, line) => sum + line.quantity, 0);
        const remaining = requested.map(item => {
          const added = total(cart, item) - total(before, item);
          if (added < 0 || added > item.quantity) attempt.uncertain = true;
          return { ...item, quantity: Math.max(0, item.quantity - Math.max(0, added)) };
        }).filter(item => item.quantity > 0);
        attempt.remaining = remaining;
        await refresh();
        error.message += attempt.uncertain ? ' Check your cart before adding again.' : ' Your cart was refreshed. Retry adds only the missing artworks.';
      } catch { attempt.uncertain = true; error.message += ' Please reload your cart to verify the result.'; }
      throw error;
    }
    // Once Shopify acknowledges the add, a section-render failure must not cause a second add.
    attempt.remaining = [];
    try {
      const normalized = await normalizeCart({ ...await cartState(), sections: result.sections });
      await refresh(normalized.sections);
    }
    catch { live('Items were added. Open the cart page to see the latest total.'); location.assign(config.cart); return result; }
    addedFeedback(); if (open) openCart(); return result;
  });
  const change = (key, quantity) => serialize(async () => {
    const result = await request(config.change + '.js', { id: key, quantity, ...sectionRequest() });
    try { await refresh((await normalizeCart(result)).sections); }
    catch (error) { await refresh().catch(() => {}); throw error; }
    live('Cart updated.');
  });
  async function updateLine(row, quantity) {
    const cart = row.closest('[data-tf-cart-content]');
    const error = cart.querySelector('[data-cart-error]');
    if (!Number.isInteger(quantity) || quantity < 0) { error.textContent = 'Please enter a whole quantity of zero or more.'; error.hidden = false; return; }
    error.hidden = true;
    cart.setAttribute('aria-busy', 'true'); const controls = [...cart.querySelectorAll('button,input')].map(el => [el, el.disabled]); controls.forEach(([el]) => el.disabled = true);
    try { await change(row.dataset.cartKey, quantity); }
    catch (e) { const current = cart.querySelector('[data-cart-error]'); current.textContent = e.message; current.hidden = false; }
    finally { cart.removeAttribute('aria-busy'); controls.forEach(([el, disabled]) => { if (el.isConnected) el.disabled = disabled; }); }
  }
  document.addEventListener('click', async event => {
    const delta = event.target.closest('[data-quantity-change]');
    const remove = event.target.closest('[data-remove-item]');
    if (delta || remove) {
      if ((delta || remove).closest('[aria-busy="true"]')) return;
      event.preventDefault(); const row = (delta || remove).closest('[data-cart-key]');
      await updateLine(row, remove ? 0 : Number(row.querySelector('[data-line-quantity]').value) + Number(delta.dataset.quantityChange));
      return;
    }
    const bundle = event.target.closest('[data-bundle-items]');
    if (bundle) {
      if (busy.has(bundle) || bundle.disabled) return;
      busy.add(bundle);
      const error = bundle.closest('.stl-footer').querySelector('[data-bundle-error]'); error.hidden = true; bundle.disabled = true;
      const original = JSON.parse(bundle.dataset.bundleItems);
      const previous = pendingBundles.get(bundle);
      const attempt = previous || {id:crypto.randomUUID(),remaining:original};
      pendingBundles.set(bundle, attempt);
      try { if (attempt.remaining.length) await add(attempt.remaining, false, attempt); else await refresh(); pendingBundles.delete(bundle); }
      catch (e) { error.textContent = e.message; error.hidden = false; }
      finally { busy.delete(bundle); bundle.disabled = Boolean(attempt.uncertain); }
    }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-line-quantity]')) updateLine(event.target.closest('[data-cart-key]'), Number(event.target.value));
    if (event.target.matches('[data-sort-select]')) event.target.form.requestSubmit();
  });
  document.addEventListener('submit', async event => {
    if (!event.target.matches('.tf-native-product-form')) return;
    event.preventDefault(); const form = event.target; if (busy.has(form) || form.dataset.uncertain) return; busy.add(form); const product = form.closest('[data-tf-product]');
    const error = product.querySelector('[data-product-error]'); const buttons = [...form.querySelectorAll('button[type="submit"]')]; const previous = buttons.map(button => button.disabled);
    error.hidden = true; buttons.forEach(button => button.disabled = true);
    try {
      const data = new FormData(form);
      const buyNow = event.submitter && event.submitter.hasAttribute('data-buy-now');
      const properties = {}; for (const [name, value] of data.entries()) { const match = /^properties\[(.+)\]$/.exec(name); if (match) properties[match[1]] = value; }
      const item = { id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1), properties };
      if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
      const attempt = {id:crypto.randomUUID()};
      try { await add([item], false, attempt); } catch(e) { if(attempt.uncertain)form.dataset.uncertain="true"; throw e; }
      if (buyNow) {
        // Submit Shopify's native checkout form, preserving locale and the complete cart.
        const checkoutForm = document.getElementById('CartDrawerForm');
        checkoutForm.requestSubmit(checkoutForm.querySelector('[name="checkout"]'));
      }
    } catch (e) { error.textContent = e.message; error.hidden = false; }
    finally { busy.delete(form); buttons.forEach((button, i) => button.disabled = Boolean(form.dataset.uncertain) || previous[i]); }
  });
  // All prices and totals come from Shopify. No browser-only cart, tax or discount arithmetic.
  window.TFShopify = { add, refresh, money, openCart, prepareCheckout: syncCart };
  document.getElementById('tf-cart-open').addEventListener('click', () => {
    if (!document.getElementById('tf-cart-modal')?.open) syncVisibleCart();
  });
  if (document.querySelector('[data-tf-cart-content="page"]')) syncVisibleCart();
  window.addEventListener('pageshow', event => {
    if (event.persisted && (document.getElementById('tf-cart-modal')?.open || document.querySelector('[data-tf-cart-content="page"]'))) syncVisibleCart();
  });
})();
